import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, Pause, Play, SkipForward, Square } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// The whole morning, playable from an emailed link — no sign-in needed.
export default function Listen() {
  const { token } = useParams();
  const [morning, setMorning] = useState(null);
  const [snag, setSnag] = useState("");
  const [at, setAt] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [paused, setPaused] = useState(false);
  const player = useRef(null);

  useEffect(() => {
    axios.get(`${API}/news/listen/${token}`)
      .then(({ data }) => setMorning(data))
      .catch((e) => setSnag(e?.response?.data?.detail || "That link has expired"));
    return () => player.current?.pause();
  }, [token]);

  const playFrom = async (index) => {
    if (!morning || index >= morning.parts.length) {
      setAt(-1);
      return;
    }
    player.current?.pause();
    setAt(index);
    setLoading(true);
    setPaused(false);
    try {
      const { data } = await axios.post(`${API}/news/listen/${token}/say`, { index },
                                        { responseType: "blob", timeout: 90000 });
      const audio = new Audio(URL.createObjectURL(data));
      player.current = audio;
      audio.onended = () => playFrom(index + 1);
      audio.onerror = () => setSnag("That part would not play — try the next one");
      await audio.play();
    } catch {
      setSnag("MiLu could not read that part out");
    } finally {
      setLoading(false);
    }
  };

  const stop = () => {
    player.current?.pause();
    player.current = null;
    setAt(-1);
    setPaused(false);
  };

  const hold = () => {
    if (!player.current) return;
    if (paused) player.current.play();
    else player.current.pause();
    setPaused(!paused);
  };

  return (
    <div className="mx-auto max-w-2xl px-5 py-16 sm:px-8" data-testid="listen-page">
      <img src="/logo.png" alt="ChatMiLu" className="h-9 w-auto" />
      {snag && <p data-testid="listen-snag" className="mt-8 text-sm text-ink-soft">{snag}</p>}
      {!morning && !snag && (
        <p className="mt-8 flex items-center gap-2 text-sm text-ink-soft">
          <Loader2 className="h-4 w-4 animate-spin" /> Getting your morning together…
        </p>
      )}

      {morning && (
        <>
          <h1 className="mt-8 font-display text-3xl font-extrabold tracking-tighter sm:text-4xl">
            {morning.greeting}
          </h1>
          <p className="mt-2 text-sm text-ink-soft md:text-base">
            Your whole morning, in order — the news, the things you follow, your digest, then the
            walks. {morning.parts.length} pieces.
          </p>

          <div className="mt-8 flex flex-wrap gap-2">
            <button
              data-testid="listen-play-all"
              onClick={() => playFrom(0)}
              className="inline-flex min-h-[46px] items-center gap-2 rounded-full bg-brand px-5 text-sm font-semibold text-white transition-colors hover:bg-brand-hover"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
              Play the whole morning
            </button>
            {at >= 0 && (
              <>
                <button data-testid="listen-hold" onClick={hold}
                        className="inline-flex min-h-[46px] items-center gap-2 rounded-full border border-white/15 px-4 text-sm text-ink-soft hover:text-ink">
                  {paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
                  {paused ? "Carry on" : "Hold on"}
                </button>
                <button data-testid="listen-skip" onClick={() => playFrom(at + 1)}
                        className="inline-flex min-h-[46px] items-center gap-2 rounded-full border border-white/15 px-4 text-sm text-ink-soft hover:text-ink">
                  <SkipForward className="h-4 w-4" /> Next
                </button>
                <button data-testid="listen-stop" onClick={stop}
                        className="inline-flex min-h-[46px] items-center gap-2 rounded-full border border-white/15 px-4 text-sm text-ink-soft hover:text-ink">
                  <Square className="h-4 w-4" /> Stop
                </button>
              </>
            )}
          </div>

          <ol className="mt-8 space-y-1.5" data-testid="listen-parts">
            {morning.parts.map((part, i) => (
              <li key={`${part.label}-${i}`}>
                <button
                  data-testid={`listen-part-${i}`}
                  onClick={() => playFrom(i)}
                  dir="auto"
                  className={`w-full rounded-2xl border px-4 py-3 text-start text-sm transition-colors ${
                    at === i ? "border-brand/60 bg-brand/10 text-brand"
                      : "border-white/10 bg-panel text-ink hover:border-brand/40"}`}
                >
                  {part.label}
                </button>
              </li>
            ))}
          </ol>
        </>
      )}
    </div>
  );
}
